import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { HighlightLayer } from '@babylonjs/core/Layers/highlightLayer';

// Side-effect imports required by HighlightLayer
import '@babylonjs/core/Layers/effectLayerSceneComponent';
import { CreateLines } from '@babylonjs/core/Meshes/Builders/linesBuilder';

import { UIController } from './UIController';
import { AnimationScheduler } from './AnimationScheduler';
import { ArrayVisualizer, ModelStyle } from '../visualizers/ArrayVisualizer';
import { LinkedListVisualizer } from '../visualizers/LinkedListVisualizer';
import { BinaryTreeVisualizer } from '../visualizers/BinaryTreeVisualizer';

// 可视化器类型
export type VisualizerType = 'array' | 'linked-list' | 'binary-tree' | 'graph';

/**
 * CodeVerse 3D 主应用类
 */
export class App {
  private canvas: HTMLCanvasElement;
  private engine: Engine;
  private scene: Scene;
  private camera: ArcRotateCamera;
  private highlightLayer: HighlightLayer;
  
  private uiController: UIController;
  private animationScheduler: AnimationScheduler;
  private arrayVisualizer: ArrayVisualizer | null = null;
  private linkedListVisualizer: LinkedListVisualizer | null = null;
  private binaryTreeVisualizer: BinaryTreeVisualizer | null = null;
  private currentVisualizerType: VisualizerType = 'array';
  
  private fpsCounter = 0;
  private lastFpsUpdate = 0;
  private showFps = true;
  private shadowsEnabled = true;
  private gridVisible = true;
  private renderQuality: 'low' | 'medium' | 'high' = 'medium';

  constructor() {
    this.canvas = document.getElementById('renderCanvas') as HTMLCanvasElement;
    
    // 创建 Babylon 引擎
    this.engine = new Engine(this.canvas, true, {
      preserveDrawingBuffer: true,
      stencil: true,
      antialias: true
    });
    
    // 创建场景 - 深色背景
    this.scene = new Scene(this.engine);
    this.scene.clearColor = new Color4(0.08, 0.09, 0.12, 1);  // 深黑灰
    
    // 创建相机
    this.camera = new ArcRotateCamera(
      'camera',
      Math.PI / 2,
      Math.PI / 3,
      15,
      Vector3.Zero(),
      this.scene
    );
    this.camera.attachControl(this.canvas, true);
    this.camera.wheelPrecision = 20;
    this.camera.minZ = 0.1;
    this.camera.lowerRadiusLimit = 5;
    this.camera.upperRadiusLimit = 50;
    
    // 创建高亮层
    this.highlightLayer = new HighlightLayer('highlight', this.scene);
    
    // 创建控制器
    this.uiController = new UIController(this);
    this.animationScheduler = new AnimationScheduler();
  }

  /**
   * 初始化应用
   */
  async initialize(): Promise<void> {
    // 设置光照
    this.setupLighting();
    
    // 设置网格地面
    this.createGridGround();
    
    // 初始化 UI 控制器
    this.uiController.initialize();
    
    // 创建默认可视化（数组）
    this.arrayVisualizer = new ArrayVisualizer(this.scene, this.highlightLayer);
    this.linkedListVisualizer = new LinkedListVisualizer(this.scene, this.highlightLayer);
    this.binaryTreeVisualizer = new BinaryTreeVisualizer(this.scene, this.highlightLayer);
    
    await this.arrayVisualizer.createFromArray([5, 3, 8, 1, 9, 2, 7, 4, 6]);
    
    // 绑定动画调度器
    this.animationScheduler.setVisualizer(this.arrayVisualizer);
    this.uiController.setAnimationScheduler(this.animationScheduler);
    
    // 生成默认的冒泡排序动画步骤
    const initialArray = [5, 3, 8, 1, 9, 2, 7, 4, 6];
    const steps = this.animationScheduler.generateBubbleSortSteps(initialArray);
    this.animationScheduler.setSteps(steps);
    this.log(`已加载冒泡排序动画 (${steps.length} 步)`, 'success');
    
    // 窗口大小调整
    window.addEventListener('resize', () => {
      this.engine.resize();
    });
    
    // 记录日志
    this.log('"算法可视化"场景初始化完成', 'success');
    this.log('已创建数组可视化 [5, 3, 8, 1, 9, 2, 7, 4, 6]', 'info');
  }

  /**
   * 设置光照 - 深色主题优化
   */
  private setupLighting(): void {
    // 环境光
    const hemisphericLight = new HemisphericLight(
      'hemisphericLight',
      new Vector3(0, 1, 0),
      this.scene
    );
    hemisphericLight.intensity = 0.8;
    hemisphericLight.diffuse = new Color3(1, 1, 1);
    hemisphericLight.groundColor = new Color3(0.2, 0.2, 0.25);
    
    // 方向光（用于阴影）
    const directionalLight = new DirectionalLight(
      'directionalLight',
      new Vector3(-1, -2, -1),
      this.scene
    );
    directionalLight.intensity = 0.5;
  }

  /**
   * 创建网格地面 - 深色主题
   */
  private createGridGround(): void {
    // 使用简单的网格线
    const gridSize = 20;
    const gridStep = 1;
    
    for (let i = -gridSize; i <= gridSize; i += gridStep) {
      // X 轴线
      const lineX = this.createLine(
        [new Vector3(-gridSize, 0, i), new Vector3(gridSize, 0, i)],
        new Color3(0.2, 0.22, 0.28)  // 深灰线
      );
      lineX.name = `gridLine_x_${i}`;
      lineX.isPickable = false;
      
      // Z 轴线
      const lineZ = this.createLine(
        [new Vector3(i, 0, -gridSize), new Vector3(i, 0, gridSize)],
        new Color3(0.2, 0.22, 0.28)  // 深灰线
      );
      lineZ.name = `gridLine_z_${i}`;
      lineZ.isPickable = false;
    }
  }

  /**
   * 创建线条
   */
  private createLine(points: Vector3[], color: Color3) {
    const lines = CreateLines('line', { points }, this.scene);
    lines.color = color;
    return lines;
  }

  /**
   * 启动渲染循环
   */
  run(): void {
    this.engine.runRenderLoop(() => {
      this.scene.render();
      this.updateFPS();
    });
  }

  /**
   * 更新 FPS 显示
   */
  private updateFPS(): void {
    this.fpsCounter++;
    const now = performance.now();
    
    if (now - this.lastFpsUpdate >= 1000) {
      const fpsEl = document.getElementById('status-fps');
      if (fpsEl) {
        fpsEl.textContent = `FPS: ${this.fpsCounter}`;
      }
      this.fpsCounter = 0;
      this.lastFpsUpdate = now;
    }
  }

  /**
   * 添加日志
   */
  log(message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info'): void {
    const logList = document.getElementById('log-list');
    if (logList) {
      const logItem = document.createElement('div');
      logItem.className = `log-item ${type}`;
      logItem.textContent = `[${new Date().toLocaleTimeString()}] ${message}`;
      logList.appendChild(logItem);
      logList.scrollTop = logList.scrollHeight;
    }
  }

  /**
   * 更新变量监视
   */
  updateVariables(variables: Record<string, unknown>): void {
    const varList = document.getElementById('variables-list');
    if (varList) {
      varList.innerHTML = '';
      for (const [name, value] of Object.entries(variables)) {
        const item = document.createElement('div');
        item.className = 'variable-item';
        item.innerHTML = `
          <span class="var-name">${name}</span>
          <span class="var-value">${JSON.stringify(value)}</span>
        `;
        varList.appendChild(item);
      }
    }
  }

  /**
   * 更新步骤显示
   */
  updateStep(current: number, total: number, description: string): void {
    const stepEl = document.getElementById('current-step');
    const descEl = document.getElementById('step-description');
    
    if (stepEl) stepEl.textContent = `👣 第 ${current} 步`;
    if (descEl) descEl.textContent = description || '继续探索...';
  }

  // Getters
  getScene(): Scene { return this.scene; }
  getEngine(): Engine { return this.engine; }
  getCamera(): ArcRotateCamera { return this.camera; }
  getHighlightLayer(): HighlightLayer { return this.highlightLayer; }
  getArrayVisualizer(): ArrayVisualizer | null { return this.arrayVisualizer; }
  getLinkedListVisualizer(): LinkedListVisualizer | null { return this.linkedListVisualizer; }
  getBinaryTreeVisualizer(): BinaryTreeVisualizer | null { return this.binaryTreeVisualizer; }
  getAnimationScheduler(): AnimationScheduler { return this.animationScheduler; }

  /**
   * 切换可视化器类型
   */
  async switchVisualizer(type: VisualizerType, data: number[], modelStyle: ModelStyle = 'cylinder'): Promise<void> {
    // 清除所有可视化器
    this.arrayVisualizer?.clear();
    this.linkedListVisualizer?.clear();
    this.binaryTreeVisualizer?.clear();
    
    this.currentVisualizerType = type;
    
    switch (type) {
      case 'array':
        await this.arrayVisualizer?.createFromArray(data, modelStyle);
        this.animationScheduler.setVisualizer(this.arrayVisualizer!, 'array');
        this.log(`切换到数组可视化 (样式: ${modelStyle})`, 'info');
        break;
      case 'linked-list':
        await this.linkedListVisualizer?.createFromArray(data);
        this.animationScheduler.setVisualizer(this.linkedListVisualizer!, 'linked-list');
        this.log(`切换到链表可视化`, 'info');
        break;
      case 'binary-tree':
        await this.binaryTreeVisualizer?.createFromArray(data);
        this.animationScheduler.setVisualizer(this.binaryTreeVisualizer!, 'binary-tree');
        this.log(`切换到二叉树可视化`, 'info');
        break;
    }
    
    // 更新状态栏
    const structEl = document.getElementById('status-structure');
    if (structEl) {
      const names: Record<VisualizerType, string> = {
        'array': '数组',
        'linked-list': '链表',
        'binary-tree': '二叉树',
        'graph': '图'
      };
      const newText = `当前: ${names[type]}`;
      if (structEl.textContent !== newText) {
        structEl.textContent = newText;
        structEl.classList.remove('status-glow');
        void structEl.offsetWidth;
        structEl.classList.add('status-glow');
      }
    }
  }

  getCurrentVisualizerType(): VisualizerType {
    return this.currentVisualizerType;
  }
  
  // ========================================
  // 渲染器设置控制方法
  // ========================================
  
  /**
   * 设置渲染质量
   */
  setRenderQuality(quality: 'low' | 'medium' | 'high'): void {
    this.renderQuality = quality;
    
    // 通过硬件缩放级别控制渲染质量
    // 值越小画质越高，值越大性能越好
    switch (quality) {
      case 'low':
        this.engine.setHardwareScalingLevel(2); // 降低分辨率，性能优先
        break;
      case 'medium':
        this.engine.setHardwareScalingLevel(1); // 标准分辨率
        break;
      case 'high':
        this.engine.setHardwareScalingLevel(0.75); // 超采样，画质优先
        break;
    }
    
    this.log(`渲染质量已设置为: ${quality === 'low' ? '性能优先' : quality === 'medium' ? '平衡模式' : '画质优先'}`, 'info');
  }
  
  /**
   * 获取当前渲染质量
   */
  getRenderQuality(): 'low' | 'medium' | 'high' {
    return this.renderQuality;
  }
  
  /**
   * 设置阴影效果
   */
  setShadowsEnabled(enabled: boolean): void {
    this.shadowsEnabled = enabled;
    
    // 调整方向光强度来模拟阴影效果
    const directionalLight = this.scene.getLightByName('directionalLight');
    if (directionalLight) {
      directionalLight.intensity = enabled ? 0.5 : 0.2;
    }
    
    this.log(`阴影效果: ${enabled ? '已开启' : '已关闭'}`, 'info');
  }
  
  /**
   * 获取阴影状态
   */
  getShadowsEnabled(): boolean {
    return this.shadowsEnabled;
  }
  
  /**
   * 设置帧率监控显示
   */
  setShowFps(show: boolean): void {
    this.showFps = show;
    
    const fpsEl = document.getElementById('status-fps');
    if (fpsEl) {
      fpsEl.style.display = show ? 'inline' : 'none';
    }
    
    this.log(`帧率监控: ${show ? '已开启' : '已关闭'}`, 'info');
  }
  
  /**
   * 获取帧率监控状态
   */
  getShowFps(): boolean {
    return this.showFps;
  }
  
  /**
   * 设置网格线显示
   */
  setGridVisible(visible: boolean): void {
    this.gridVisible = visible;
    
    // 遍历场景中的网格线
    this.scene.meshes.forEach(mesh => {
      if (mesh.name.startsWith('gridLine_')) {
        mesh.isVisible = visible;
      }
    });
    
    this.log(`网格线: ${visible ? '已显示' : '已隐藏'}`, 'info');
  }
  
  /**
   * 获取网格线状态
   */
  getGridVisible(): boolean {
    return this.gridVisible;
  }
  
  /**
   * 获取渲染器设置
   */
  getRendererSettings(): {
    shadowsEnabled: boolean;
    showFps: boolean;
    gridVisible: boolean;
    renderQuality: 'low' | 'medium' | 'high';
  } {
    return {
      shadowsEnabled: this.shadowsEnabled,
      showFps: this.showFps,
      gridVisible: this.gridVisible,
      renderQuality: this.renderQuality
    };
  }
}
